import { useState, useRef, useEffect } from 'react'
import { useHyperliquidWallet } from './HyperliquidWalletContext'
import { isBiometricAvailable } from '../../utils/webauthnPrf'
import { FingerprintIcon, PadlockIcon } from '../WalletIcons'
import { getRememberPref, setRememberPref } from '../../utils/unlockCache'

const num = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : 0
}
const fmtUsd = (n) => (n == null ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)

export default function HyperliquidWalletSetup() {
  const {
    maskedAddress, isLocked, hasStoredKeys,
    importKeys, unlock, lock, removeKeys,
    hasBiometric, enableBiometric, unlockWithBiometric, disableBiometric,
  } = useHyperliquidWallet()

  const [mode, setMode] = useState(null)       // null | 'import' | 'unlock'
  const [rememberOn, setRememberOn] = useState(getRememberPref)
  const [privateKeyInput, setPrivateKeyInput] = useState('')
  const [accountInput, setAccountInput] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // Platform-authenticator probe; see WalletSetup for why this can't gate
  // first paint and why it doesn't prove PRF on its own.
  const [bioAvailable, setBioAvailable] = useState(false)
  // ─── Futures account value ───
  // useTrading's hl_poll broadcasts the account frame on a window event, so
  // this menu reads the SAME numbers the position tags and WalletHoldings do
  // — no second poller, no second source that can disagree. The event only
  // fires while a futures chart is polling, so `null` here means "not
  // polling yet", NOT "zero" — the row shows — rather than a false $0.00.
  const [hlBal, setHlBal] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
        setMode(null)
        setError('')
        setPassword('')
        setConfirmPw('')
        setPrivateKeyInput('')
        setAccountInput('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (mode && inputRef.current) inputRef.current.focus()
  }, [mode])

  useEffect(() => {
    let alive = true
    isBiometricAvailable().then(ok => { if (alive) setBioAvailable(ok) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    const handler = (e) => setHlBal(e.detail || null)
    window.addEventListener('solcheddar-hl-balance', handler)
    return () => window.removeEventListener('solcheddar-hl-balance', handler)
  }, [])

  const handleImport = async () => {
    if (!accountInput.trim()) return setError('Enter your account address')
    if (!privateKeyInput.trim()) return setError('Enter your API wallet private key')
    if (!password) return setError('Create a password')
    if (password !== confirmPw) return setError('Passwords don\'t match')
    if (password.length < 4) return setError('Password too short (min 4)')

    setLoading(true)
    setError('')
    try {
      await importKeys(privateKeyInput.trim(), accountInput.trim(), password)
      setPrivateKeyInput('')
      setAccountInput('')
      setPassword('')
      setConfirmPw('')
      setMode(null)
      setShowDropdown(false)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const handleUnlock = async () => {
    if (!password) return setError('Enter your password')
    setLoading(true)
    setError('')
    try {
      await unlock(password)
      setPassword('')
      setMode(null)
      setShowDropdown(false)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const handleRemove = () => {
    if (confirm('Remove Hyperliquid keys? You\'ll need to re-import.')) {
      removeKeys()
      setMode(null)
      setShowDropdown(false)
    }
  }

  const handleBioUnlock = async () => {
    setLoading(true)
    setError('')
    try {
      await unlockWithBiometric()
      setPassword('')
      setMode(null)
      setShowDropdown(false)
    } catch (err) {
      // Cancelled, or the authenticator can't derive a key. Either way the
      // password is the way in, so open the menu ON it rather than leaving a
      // dead-end error under a button that just failed.
      setError(err.message)
      setMode('unlock')
      setShowDropdown(true)
    }
    setLoading(false)
  }

  const handleEnableBio = async () => {
    setError('')
    try {
      await enableBiometric()
      setShowDropdown(false)
    } catch (err) {
      setError(err.message)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (mode === 'import') handleImport()
      else if (mode === 'unlock') handleUnlock()
    }
  }

  // ─── Unlocked: show masked address + dropdown ───
  if (hasStoredKeys && !isLocked && maskedAddress) {
    return (
      <div className="local-wallet-wrapper" ref={dropdownRef}>
        <button
          className="local-wallet-btn connected"
          onClick={() => setShowDropdown(p => !p)}
          style={{ borderColor: '#46d41f' }}
        >
          {maskedAddress}
        </button>

        {showDropdown && (
          <div className="local-wallet-dropdown">
            {/* Same geometry as the Solana menu: small inline padlock at the
                head of the address row, full-height fingerprint tile only in
                the locked state. Two wallets, one muscle memory. */}
            <div className="lw-addr-row">
              <button
                className="lw-lock-mini"
                onClick={() => { lock(); setShowDropdown(false) }}
                title="Lock keys"
                aria-label="Lock keys"
              >
                <PadlockIcon />
              </button>
              <div className="lw-dropdown-addr" title="Hyperliquid Account">
                {maskedAddress}
              </div>
            </div>
            {/* EQUITY = available + margin posted + unrealized PnL. That's the
                number that answers "what is this account worth right now",
                which is what a wallet menu is asked for — available alone
                understates it by whatever is locked in open positions. Both
                are shown because they answer different questions: equity is
                worth, available is buying power. */}
            <div
              className="lw-dropdown-addr"
              title={hlBal
                ? 'Equity = available + posted margin + unrealized PnL'
                : 'Open a futures chart to start polling the account'}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, opacity: 0.9 }}
            >
              <span style={{ opacity: 0.6 }}>Equity</span>
              <span style={{ color: '#c8cfdc', fontWeight: 600 }}>
                {hlBal ? fmtUsd(num(hlBal.available) + num(hlBal.margin) + num(hlBal.unrealizedPnl)) : '—'}
              </span>
            </div>
            <div
              className="lw-dropdown-addr"
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, opacity: 0.9 }}
            >
              <span style={{ opacity: 0.6 }}>Available</span>
              <span style={{ color: '#c8cfdc', fontWeight: 600 }}>
                {hlBal ? fmtUsd(num(hlBal.available)) : '—'}
              </span>
            </div>
            {(bioAvailable || hasBiometric) && (
              hasBiometric ? (
                <button className="lw-dropdown-action" onClick={disableBiometric}>
                  Turn off fingerprint unlock
                </button>
              ) : (
                <button className="lw-dropdown-action" onClick={handleEnableBio}>
                  Enable fingerprint unlock
                </button>
              )
            )}
            {error && <div className="lw-error">{error}</div>}
            <button className="lw-dropdown-action danger" onClick={handleRemove}>
              Remove Keys
            </button>
          </div>
        )}
      </div>
    )
  }

  // ─── Locked: connect button ───
  if (hasStoredKeys && isLocked) {
    return (
      <div className="local-wallet-wrapper" ref={dropdownRef}>
        {/* Enrolled → the print replaces the word, and tapping it goes
            straight to the biometric prompt. The LABEL STAYS IN THE DOM at
            opacity 0: it's what sizes the button, so the header can't shift
            by a few px depending on whether a passkey exists. The icon is
            absolutely centred over it. */}
        <button
          className={`local-wallet-btn locked${hasBiometric ? ' has-bio' : ''}`}
          onClick={hasBiometric
            ? handleBioUnlock
            : () => { setMode('unlock'); setShowDropdown(true); setError('') }}
          title={hasBiometric ? 'Connect with fingerprint' : undefined}
          aria-label={hasBiometric ? 'Connect with fingerprint' : undefined}
        >
          <span className="lw-btn-label">Connect</span>
          {hasBiometric && (
            <span className="lw-btn-icon">
              <FingerprintIcon style={{ width: 'auto', height: '100%' }} />
            </span>
          )}
        </button>

        {showDropdown && mode === 'unlock' && (
          <div className="local-wallet-dropdown">
            {/* Title inside the column — see WalletSetup for why. It's what
                the fingerprint centres against, so lifting it above the split
                puts the print ~11.6px low again. */}
            <div className="lw-dd-split">
            <div className="lw-dd-main">
            <div className="lw-dropdown-title">Connect Hyperliquid Keys</div>
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Password"
              className="lw-input"
            />
            <label className="lw-remember" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, opacity: 0.8, margin: '4px 0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={rememberOn}
                onChange={(e) => { setRememberOn(e.target.checked); setRememberPref(e.target.checked) }}
              />
              Remember for 12h
            </label>
            {error && <div className="lw-error">{error}</div>}
            <button
              className="lw-dropdown-action primary"
              onClick={handleUnlock}
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>
            <button className="lw-dropdown-action danger" onClick={handleRemove}>
              Remove & Re-import
            </button>
            </div>
            {/* First run is always the password — the biometric envelope is
                encrypted with the keys themselves, which only exist in memory
                after a password connect. See WalletSetup for the full note on
                the ghost/amber state model and why the stamp is one word. */}
            <button
              className={`lw-icon-side${hasBiometric ? ' is-live' : ''}`}
              onClick={handleBioUnlock}
              disabled={!hasBiometric || loading}
              title={hasBiometric
                ? 'Connect with fingerprint'
                : 'Connect with your password once, then enable fingerprint unlock'}
              aria-label="Connect with fingerprint"
            >
              <FingerprintIcon />
              {!hasBiometric && (
                <span className="lw-icon-stamp">password</span>
              )}
            </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─── No keys: connect button ───
  return (
    <div className="local-wallet-wrapper" ref={dropdownRef}>
      <button
        className="local-wallet-btn no-wallet"
        onClick={() => { setMode('import'); setShowDropdown(true); setError('') }}
        style={{ color: '#46d41f', borderColor: '#46d41f55' }}
      >
        + Connect
      </button>

      {showDropdown && mode === 'import' && (
        <div className="local-wallet-dropdown import-mode">
          <div className="lw-dropdown-title">Connect Hyperliquid Keys</div>
          <div className="lw-dropdown-hint">
            Stored AES-256 encrypted in your browser. Keys are sent to your server for order signing — never to any third party.
          </div>
          <input
            ref={inputRef}
            type="text"
            value={accountInput}
            onChange={(e) => setAccountInput(e.target.value)}
            placeholder="Account address (0x... login wallet)"
            className="lw-input"
            autoComplete="off"
          />
          <input
            type="password"
            value={privateKeyInput}
            onChange={(e) => setPrivateKeyInput(e.target.value)}
            placeholder="API wallet private key (0x...)"
            className="lw-input"
            autoComplete="off"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Create password"
            className="lw-input"
          />
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Confirm password"
            className="lw-input"
          />
          {error && <div className="lw-error">{error}</div>}
          <button
            className="lw-dropdown-action primary"
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? 'Encrypting...' : 'Import & Encrypt'}
          </button>
        </div>
      )}
    </div>
  )
}